import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { PrimaryButton, inputClass, ringStyle } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import TrackStatusChip from "./TrackStatusChip";
import { EditTrackDialog, TakeDownDialog } from "./TrackDialogs";
import { MESSAGES, loadGenres, trackApi, trackErrorMessage } from "./trackApi";

export const TRACK_TABS = [];

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const SORTS = {
  newest: { label: "Sort: Newest", fn: (a, b) => new Date(b.created_at) - new Date(a.created_at) },
  oldest: { label: "Sort: Oldest", fn: (a, b) => new Date(a.created_at) - new Date(b.created_at) },
  streams: {
    label: "Sort: Most Streams",
    // Tracks without stream data go last.
    fn: (a, b) => (b.stream_count ?? -1) - (a.stream_count ?? -1) || new Date(b.created_at) - new Date(a.created_at),
  },
  title: { label: "Sort: Title A-Z", fn: (a, b) => (a.title || "").localeCompare(b.title || "") },
};

// Only a published track is on the public page. An approved track is waiting to be published, so it is not counted as live.
export const isLive = (t) => t.display_status === "published";
const canLicense = (t) => ["approved", "published"].includes(t.display_status);

// The status filter offers the four groups in the ticket; each covers the detailed statuses behind it.
const STATUS_GROUPS = {
  live: { label: "Live", match: isLive },
  review: { label: "Under Review", match: (t) => ["pending_review", "approved"].includes(t.display_status) },
  draft: {
    label: "Draft",
    match: (t) => ["draft", "ready_for_review", "upload_pending", "uploading", "uploaded", "processing", "upload_failed", "processing_failed"].includes(t.display_status),
  },
  rejected: { label: "Rejected", match: (t) => t.display_status === "rejected" },
};

const formatStreams = (value) => (Number.isFinite(value) ? value.toLocaleString() : "—");

const Icon = ({ d }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const EYE = "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z";
const PENCIL = "M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z";

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-6 py-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-1" data-testid={`stat-${label}`}>{value}</p>
    </div>
  );
}

function Thumb({ track }) {
  return (
    <div className="w-12 h-12 rounded-lg bg-[#e9e4dc] overflow-hidden shrink-0 flex items-center justify-center text-gray-500">
      {track.artwork?.url ? <img src={track.artwork.url} alt="" className="w-full h-full object-cover" /> : <Icon d="M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />}
    </div>
  );
}

function TrackRow({ track, onRetry, retrying, onEditLive, onTakeDown }) {
  const failedUpload = track.display_status === "upload_failed";
  const failedProcessing = track.display_status === "processing_failed";
  const to = `/artist-studio/tracks/${track.id}`;
  const viewTo = `${to}/view`;
  return (
    <tr className="border-t border-gray-100 align-top" data-testid="track-row">
      <td className="px-5 py-4">
        <div className="flex items-center gap-3 min-w-[14rem]">
          <Thumb track={track} />
          <div className="min-w-0">
            <Link to={viewTo} className="font-semibold text-gray-900 hover:underline block truncate">{track.title || "Untitled draft"}</Link>
            <span className="text-sm text-gray-500">
              {track.artist_name ? `${track.artist_name} · ` : ""}<span className="capitalize">{track.kind}</span>
            </span>
          </div>
        </div>
        {track.display_status === "rejected" && track.rejection_reason && (
          <p className="mt-2 text-sm rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 max-w-sm">
            <span className="font-semibold">Rejected:</span> {track.rejection_reason}
          </p>
        )}
        {track.display_status === "rejected" && track.appeal_status === "pending" && (
          <p className="mt-2 text-sm text-amber-800 max-w-sm" data-testid="row-appeal-status">Appeal under review</p>
        )}
        {track.display_status === "rejected" && track.appeal_status === "denied" && (
          <p className="mt-2 text-sm text-red-700 max-w-sm" data-testid="row-appeal-status">Appeal denied. This decision is final.</p>
        )}
        {failedUpload && <p className="mt-2 text-sm text-red-700 max-w-sm">{MESSAGES.uploadFailed}</p>}
        {failedProcessing && <p className="mt-2 text-sm text-red-700 max-w-sm">{MESSAGES.processingFailed}</p>}
      </td>
      <td className="px-5 py-4 text-gray-600">{track.release_name || "—"}</td>
      <td className="px-5 py-4 text-gray-600">{track.genre?.name || "—"}</td>
      <td className="px-5 py-4"><TrackStatusChip status={track.display_status} /></td>
      <td className="px-5 py-4 text-gray-600 whitespace-nowrap" data-testid="track-streams">{formatStreams(track.stream_count)}</td>
      <td className="px-5 py-4 text-gray-600 whitespace-nowrap">{formatDate(track.created_at)}</td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-3 text-gray-700">
          <Link to={viewTo} aria-label={`View ${track.title || "draft"}`} title="View"><Icon d={EYE} /></Link>
          {track.can_edit && <Link to={to} aria-label={`Edit ${track.title || "draft"}`} title="Edit"><Icon d={PENCIL} /></Link>}
          {!track.can_edit && track.can_edit_live && (
            <button type="button" aria-label={`Edit ${track.title}`} title="Edit Track" onClick={() => onEditLive(track)} data-testid="row-edit"><Icon d={PENCIL} /></button>
          )}
          {track.can_take_down && (
            <button type="button" className="text-sm font-semibold text-red-600 hover:underline" onClick={() => onTakeDown(track)} data-testid="row-take-down">Take Down</button>
          )}
          {failedProcessing && (
            <button type="button" className="text-sm font-semibold underline disabled:opacity-50" disabled={retrying} onClick={() => onRetry(track)}>
              {retrying ? "Retrying…" : "Retry Processing"}
            </button>
          )}
          {failedUpload && <Link to={to} className="text-sm font-semibold underline">Retry upload</Link>}
          {track.can_resubmit && <Link to={`${to}?resubmit=1`} className="text-sm font-semibold underline" data-testid="row-resubmit">Resubmit</Link>}
          {track.can_appeal && <Link to={viewTo} className="text-sm font-semibold underline" data-testid="row-appeal">Appeal</Link>}
          {canLicense(track) && <Link to={`/artist-studio/monetization?track=${track.id}`} className="text-sm font-semibold underline">Licensing</Link>}
        </div>
      </td>
    </tr>
  );
}

function CatalogueBody() {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState(null);
  const [catalogueGenres, setCatalogueGenres] = useState([]);
  const [error, setError] = useState("");
  const [retryingId, setRetryingId] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [genre, setGenre] = useState("");
  const [sort, setSort] = useState("newest");
  const [editing, setEditing] = useState(null);
  const [takingDown, setTakingDown] = useState(null);
  const [busy, setBusy] = useState(false);

  const replaceTrack = (updated) => setTracks((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));

  const confirmTakeDown = async () => {
    if (busy || !takingDown) return;
    setBusy(true);
    try {
      const updated = await trackApi.takeDown(takingDown.id);
      replaceTrack(updated);
      toast.success(updated.display_status === "archived" ? `“${takingDown.title}” was taken down. Existing licences remain valid.` : "The track was withdrawn from review.");
      setTakingDown(null);
    } catch (e) {
      // The row is left as it was: a failed takedown is never shown as done.
      toast.error(`${trackErrorMessage(e, MESSAGES[500])} The track has not been taken down.`);
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    setError("");
    try {
      setTracks(await trackApi.list());
    } catch (e) {
      setError(trackErrorMessage(e, MESSAGES[500]));
      setTracks([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The genre filter lists every genre in the catalogue; if that list can't be loaded it falls back to the artist's own genres.
  useEffect(() => {
    let cancelled = false;
    loadGenres()
      .then((rows) => !cancelled && setCatalogueGenres(rows.map((g) => g.name).filter(Boolean)))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const retry = async (track) => {
    if (retryingId) return;
    setRetryingId(track.id);
    try {
      const updated = await trackApi.retry(track.id);
      setTracks((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
      toast.success("Your audio is ready.");
    } catch (e) {
      toast.error(trackErrorMessage(e, MESSAGES.processingFailed));
      load();
    } finally {
      setRetryingId(null);
    }
  };

  const view = useMemo(() => {
    const rows = tracks || [];
    const q = query.trim().toLowerCase();
    return rows
      .filter((t) => !status || STATUS_GROUPS[status].match(t))
      .filter((t) => !genre || t.genre?.name === genre)
      .filter((t) => !q || [t.title, t.artist_name, t.release_name, t.genre?.name].some((v) => (v || "").toLowerCase().includes(q)))
      .sort(SORTS[sort].fn);
  }, [tracks, query, status, genre, sort]);

  if (tracks === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loading />
      </div>
    );
  }

  const genres = Array.from(new Set([...catalogueGenres, ...tracks.map((t) => t.genre?.name).filter(Boolean)])).sort((a, b) => a.localeCompare(b));
  const releases = new Set(tracks.map((t) => t.release_name).filter(Boolean)).size;

  if (!error && tracks.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 py-16 px-6 text-center">
        <p className="text-4xl" aria-hidden="true">♪</p>
        <h2 className="text-xl font-bold text-gray-900 mt-3">You haven't uploaded any tracks yet</h2>
        <p className="text-gray-600 mt-1">Upload your first track to start building your audience and earnings.</p>
        <PrimaryButton className="mt-6" onClick={() => navigate("/artist-studio/tracks/new")}>Upload Track</PrimaryButton>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <p className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">
          {error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
        </p>
      )}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total tracks" value={tracks.length} />
        <StatCard label="Albums / releases" value={releases} />
        <StatCard label="Live tracks" value={tracks.filter(isLive).length} />
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-3">
        <input
          type="search"
          className={`${inputClass(false)} lg:flex-1`}
          style={ringStyle}
          placeholder="Search catalogue by track, artist or release"
          aria-label="Search catalogue"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className={`${inputClass(false)} lg:w-44`} style={ringStyle} aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_GROUPS).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
        </select>
        <select className={`${inputClass(false)} lg:w-40`} style={ringStyle} aria-label="Filter by genre" value={genre} onChange={(e) => setGenre(e.target.value)}>
          <option value="">All genres</option>
          {genres.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className={`${inputClass(false)} lg:w-44`} style={ringStyle} aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <p className="text-sm text-gray-500 mb-3" data-testid="showing">Showing {view.length} of {tracks.length} tracks</p>

      <div className="rounded-2xl border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-left text-[15px]">
          <thead className="bg-[#f1f6f2] text-xs font-bold uppercase tracking-wide" style={{ color: "#095228" }}>
            <tr>
              <th className="px-5 py-4">Track</th>
              <th className="px-5 py-4">Release</th>
              <th className="px-5 py-4">Genre</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Streams</th>
              <th className="px-5 py-4">Uploaded</th>
              <th className="px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {view.map((t) => (
              <TrackRow key={t.id} track={t} onRetry={retry} retrying={retryingId === t.id} onEditLive={setEditing} onTakeDown={setTakingDown} />
            ))}
          </tbody>
        </table>
        {view.length === 0 && <p className="p-6 text-center text-gray-500">No tracks match your search.</p>}
      </div>
      {editing && (
        <EditTrackDialog
          track={editing}
          onCancel={() => setEditing(null)}
          onSaved={(updated) => {
            replaceTrack(updated);
            setEditing(null);
          }}
        />
      )}
      {takingDown && <TakeDownDialog track={takingDown} busy={busy} onCancel={() => !busy && setTakingDown(null)} onConfirm={confirmTakeDown} />}
    </div>
  );
}

export default function TrackList() {
  const account = useStudioAccount();
  const navigate = useNavigate();
  return (
    <StudioGate
      account={account}
      active="tracks"
      heading="My Catalogue"
      tabs={TRACK_TABS}
      maxWidth="max-w-6xl"
      headingAction={
        <PrimaryButton onClick={() => navigate("/artist-studio/tracks/new")} className="shrink-0 !px-6 !py-3">
          Upload Track
        </PrimaryButton>
      }
    >
      <CatalogueBody />
    </StudioGate>
  );
}

